/**
 * Scene file operations slice for the Zustand store.
 *
 * Contains new scene, save/load .pr files, and .blend file merging.
 */

import { parseBlendFileAsync } from "../blend-reader/parseBlendFileAsync.ts";
import { DEFAULT_CURSOR_3D } from "../modeling/cursor3d.ts";
import { DriverGraph } from "../scene-graph/DriverGraph.ts";
import { showInvalidSceneObjectsDialog } from "../ui/InvalidSceneObjectsDialog.tsx";
import { clearModifierCache } from "../viewport/modifierApply.ts";
import { setActivePage } from "./activePage.ts";
import {
  allBlendObjectNames,
  allShapeKeySets,
  ensureShapeKeyWeights,
  findEntryByHash,
  generateBlendFileId,
  initBlendVisibility,
} from "./blendFileQueries.ts";
import {
  getFileHandle,
  hasFileSystemAccess,
  saveWithFileSystemAccess,
  setFileHandle,
  triggerDownload,
} from "./fileSystemAccess.ts";
import {
  applySceneDocument,
  captureSceneDocument,
} from "./persistence/sceneDocument.ts";
import {
  parseSceneDocument,
  serializeSceneDocument,
} from "./persistence/sceneDocumentIO.ts";
import { clearSessionState } from "./persistence.ts";
import {
  DEFAULT_RENDER_RENDER_CONFIG,
  DEFAULT_VIEWPORT_RENDER_CONFIG,
} from "./renderConfig.ts";
import { setCurrentLibraryItemId } from "./saveToLibrary.ts";
import { clearLiveEditPreview } from "./liveEditPreview.ts";
import { suppressDirtyTracking } from "./storeDirtyTracking.ts";
import type {
  AppState,
  BlendFileEntry,
  InteractionMode,
} from "./storeTypes.ts";
import { IDENTITY_GROUP_TRANSFORM } from "./storeTypes.ts";
import { undoManager } from "./storeUndo.ts";

type Set_ = (partial: Partial<AppState>) => void;
type Get_ = () => AppState;

// ---------------------------------------------------------------------------
// Three-word scene name generator
// ---------------------------------------------------------------------------

const ADJECTIVES = [
  "bold",
  "calm",
  "dark",
  "faint",
  "grand",
  "harsh",
  "keen",
  "light",
  "mild",
  "neat",
  "pale",
  "quick",
  "rare",
  "sharp",
  "tall",
  "vast",
  "warm",
  "young",
  "brief",
  "crisp",
  "dense",
  "flat",
  "grim",
  "hollow",
  "iron",
  "jade",
  "large",
  "muted",
  "narrow",
  "open",
  "plain",
  "round",
  "slim",
  "thick",
  "vivid",
  "wide",
  "amber",
  "blunt",
  "coarse",
  "deep",
  "even",
  "fresh",
  "glass",
  "heavy",
  "lucid",
  "mossy",
  "noble",
  "rapid",
  "silent",
  "stone",
];

const NOUNS = [
  "arrow",
  "blade",
  "cliff",
  "dune",
  "ember",
  "fern",
  "gate",
  "helm",
  "isle",
  "jewel",
  "knot",
  "lance",
  "marsh",
  "nest",
  "oak",
  "prism",
  "quill",
  "ridge",
  "shard",
  "torch",
  "urn",
  "vault",
  "wren",
  "yarn",
  "anvil",
  "brook",
  "cairn",
  "drum",
  "flint",
  "grove",
  "haze",
  "idol",
  "jade",
  "keel",
  "loom",
  "mast",
  "orbit",
  "plume",
  "reef",
  "spoke",
  "tide",
  "vine",
  "well",
  "arch",
  "bone",
  "crest",
  "drift",
  "forge",
  "grain",
  "stone",
];

const AGENTS = [
  "archer",
  "baker",
  "clerk",
  "dancer",
  "elder",
  "fencer",
  "guard",
  "herald",
  "inker",
  "jester",
  "keeper",
  "lancer",
  "mason",
  "nomad",
  "oracle",
  "painter",
  "ranger",
  "scribe",
  "tanner",
  "usher",
  "vassal",
  "warden",
  "weaver",
  "carver",
  "drafter",
  "etcher",
  "forger",
  "glazier",
  "hunter",
  "joiner",
  "knight",
  "logger",
  "marker",
  "nailer",
  "packer",
  "rider",
  "sailor",
  "tailor",
  "turner",
  "vendor",
  "walker",
  "angler",
  "binder",
  "caster",
  "diver",
  "falconer",
  "gilder",
  "harper",
  "mender",
  "masker",
];

function pickRandom<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

/** Generate a memorable three-word scene name like "bold arrow masker". */
export function generateSceneName(): string {
  return `${pickRandom(ADJECTIVES)} ${pickRandom(NOUNS)} ${pickRandom(AGENTS)}`;
}

/** Scene file store methods — call createSceneFileOps(set, get) inside the store factory. */
export function createSceneFileOps(set: Set_, get: Get_) {
  return {
    newScene: () => {
      undoManager.clear();
      setFileHandle(null);
      clearSessionState();
      clearModifierCache();
      setCurrentLibraryItemId(null);
      clearLiveEditPreview();
      suppressDirtyTracking(true);
      set({
        blendFiles: new Map(),
        driverGraph: null,
        selectedObjectTargets: [],
        collections: new Map(),
        visibility: new Map(),
        renderVisibility: new Map(),
        shadingOverrides: new Map(),
        shapeKeyWeights: new Map(),
        modifierOverrides: new Map(),
        sceneObjects: new Map(),
        materials: new Map(),
        mannequinMorphs: new Map(),
        editMode: null,
        poseMode: null,
        mode: "navigate" as InteractionMode,
        workspaceMode: "object",
        commandBuffer: null,
        commandPreSnapshot: null,
        commandCancelSnapshot: null,
        focalLength: 50,
        cameraPosition: [0, -3, 1] as [number, number, number],
        cameraTarget: [0, 0, 0.5] as [number, number, number],
        cursor3D: {
          ...DEFAULT_CURSOR_3D,
          position: [...DEFAULT_CURSOR_3D.position] as [
            number,
            number,
            number,
          ],
        },
        referenceLayers: [],
        activeReferenceLayerId: null,
        cameraBookmarks: [],
        renderConfigs: {
          viewport: { ...DEFAULT_VIEWPORT_RENDER_CONFIG },
          render: { ...DEFAULT_RENDER_RENDER_CONFIG },
        },
        canUndo: false,
        canRedo: false,
        favoriteShapeKeys: new Set(),
        currentSceneFileName: `${generateSceneName()}.pr`,
        sceneDirty: false,
        sceneCreatedAt: null,
        error: null,
        loading: false,
      });
      suppressDirtyTracking(false);
    },

    saveSceneToFile: async (forceNewFile = false) => {
      const state = get();
      const raw = captureSceneDocument(state);
      const json = await serializeSceneDocument(raw);
      const blob = new Blob([json], { type: "application/json" });

      const baseName =
        state.currentSceneFileName ??
        (() => {
          const d = new Date();
          const pad2 = (n: number) => String(n).padStart(2, "0");
          return `Scene ${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}.${pad2(d.getMinutes())}.pr`;
        })();

      if (hasFileSystemAccess()) {
        const existingHandle = forceNewFile ? null : getFileHandle();
        const handle = await saveWithFileSystemAccess(
          blob,
          existingHandle,
          baseName,
        );
        if (handle) {
          setFileHandle(handle);
          set({
            currentSceneFileName: handle.name,
            sceneDirty: false,
          });
        }
      } else {
        // Fallback: trigger download
        triggerDownload(blob, baseName);
        set({
          currentSceneFileName: baseName,
          sceneDirty: false,
        });
      }
    },

    loadSceneFromFile: async (file: File) => {
      try {
        set({ loading: true, error: null });
        const text = await file.text();
        const sceneData = await parseSceneDocument(text);
        const { patch, omittedObjects } = applySceneDocument(sceneData, get());
        if (omittedObjects.length > 0) {
          const sceneName = file.name.replace(/\.pr$/i, "");
          const result = await showInvalidSceneObjectsDialog(
            sceneName,
            omittedObjects,
          );
          if (result === "back") {
            set({ loading: false });
            setActivePage("library");
            return;
          }
        }

        // Clear undo stacks for fresh session
        undoManager.clear();
        setFileHandle(null);
        patch.canUndo = false;
        patch.canRedo = false;
        patch.loading = false;
        patch.currentSceneFileName = file.name;
        patch.sceneDirty = false;

        // Resolve blend file references against the loaded entries.
        const currentState = get();
        const refs = sceneData.blendFileRefs;

        if (refs.length > 0) {
          const newFiles = new Map<string, BlendFileEntry>();
          const vis = patch.visibility as Map<string, boolean>;

          for (const ref of refs) {
            const matchingEntry = findEntryByHash(
              currentState.blendFiles,
              ref.sha256,
            );
            if (!matchingEntry) continue;
            const [entryId, entry] = matchingEntry;
            // Restore group transform from the saved entry id, or identity.
            const savedGt = sceneData.groupTransforms[entryId];
            const groupTransform = savedGt
              ? {
                  position: [...savedGt.position] as [number, number, number],
                  rotation: [...savedGt.rotation] as [
                    number,
                    number,
                    number,
                    number,
                  ],
                  scale: [...savedGt.scale] as [number, number, number],
                }
              : { ...IDENTITY_GROUP_TRANSFORM };
            newFiles.set(entryId, { ...entry, groupTransform });
            initBlendVisibility(entry.file.objects, vis);
          }

          if (newFiles.size > 0) {
            patch.blendFiles = newFiles;

            // Reconcile saved weights with actual shape key sets — the .pr file
            // omits all-zero sets for compactness.
            ensureShapeKeyWeights(
              newFiles,
              patch.shapeKeyWeights as Map<string, Map<string, number>>,
            );

            // Rebuild driver graph from ALL matched entries' shape key sets
            const mergedSKSets = allShapeKeySets(newFiles);
            if (mergedSKSets.size > 0) {
              const driverGraph = new DriverGraph();
              driverGraph.build(mergedSKSets);
              if (patch.shapeKeyWeights) {
                driverGraph.evaluate(
                  patch.shapeKeyWeights as Map<string, Map<string, number>>,
                );
              }
              patch.driverGraph = driverGraph;
            } else if (currentState.driverGraph && patch.shapeKeyWeights) {
              currentState.driverGraph.evaluate(
                patch.shapeKeyWeights as Map<string, Map<string, number>>,
              );
            }
          } else {
            // No matching blend files in memory — clear blend state
            patch.blendFiles = new Map();
            patch.driverGraph = null;
          }
        } else {
          // Scene has no blend file references
          patch.blendFiles = new Map();
          patch.driverGraph = null;
        }

        // Invalidate modifier geometry cache — R3F auto-disposes GPU
        // resources on unmount, so cached modifier results are stale.
        clearModifierCache();
        clearLiveEditPreview();
        suppressDirtyTracking(true);
        set(patch as Partial<AppState>);
        suppressDirtyTracking(false);
        const next = get();
        window.__poseref_cameraController?.applyState({
          projection: next.cameraProjection,
          position: next.cameraPosition,
          target: next.cameraTarget,
          up: next.cameraUp,
          focalLength: next.focalLength,
          orthographicHalfHeight: next.orthographicHalfHeight,
        });

        // Resolve reference image URLs from R2 keys
        get().refreshReferenceImageUrls();
      } catch (e) {
        suppressDirtyTracking(false);
        set({
          error: e instanceof Error ? e.message : String(e),
          loading: false,
        });
      }
    },

    /**
     * Add/merge a blend file into the current scene from pre-parsed data.
     * Called by importBlendAndLoad after it has already read the buffer and
     * computed the hash, so no redundant I/O or hashing occurs.
     *
     * Unlike loadFile, this never replaces the scene — it only adds blend
     * content alongside whatever already exists (scene objects, mannequins,
     * undo history, selection, etc. are all preserved).
     */
    addBlendFileParsed: async (
      buffer: ArrayBuffer,
      hash: string,
      fileName: string,
    ) => {
      try {
        set({ loading: true, error: null });
        const blendFile = await parseBlendFileAsync(buffer.slice(0));

        const currentState = get();

        // Build name collision map against ALL existing entries' object names
        const existingNames = allBlendObjectNames(currentState.blendFiles);
        const baseName = fileName.replace(/\.blend$/i, "");
        const remap = new Map<string, string>();
        for (const obj of blendFile.objects) {
          if (existingNames.has(obj.name)) {
            const newName = `${baseName}_${obj.name}`;
            remap.set(obj.name, newName);
          }
        }

        // Remap objects — only object names and parent references need
        // remapping.  meshRef and shapeKeySetRef are internal keys within
        // the entry's own mesh/shape-key-set maps, so they stay unchanged.
        const newObjects = blendFile.objects.map((obj) => {
          const remapped = { ...obj };
          if (remap.has(obj.name)) remapped.name = remap.get(obj.name)!;
          if (obj.parentName && remap.has(obj.parentName)) {
            remapped.parentName = remap.get(obj.parentName)!;
          }
          return remapped;
        });

        // Mesh and shape-key-set maps are keyed by their own internal
        // raw names (e.g. "MECube", "Key") — these are entry-scoped and
        // never collide across entries, so no remapping is needed.
        const remappedMeshes = blendFile.meshes;
        const remappedSKSets = blendFile.shapeKeySets;

        // Create the new independent entry (NO merging)
        const newEntryId = generateBlendFileId();
        const newEntry: BlendFileEntry = {
          id: newEntryId,
          file: {
            ...blendFile,
            objects: newObjects,
            meshes: remappedMeshes,
            shapeKeySets: remappedSKSets,
          },
          fileName,
          hash,
          originalBuffer: buffer,
          groupTransform: { ...IDENTITY_GROUP_TRANSFORM },
        };

        const newFiles = new Map(currentState.blendFiles);
        newFiles.set(newEntryId, newEntry);

        // Initialize visibility for new objects (preserve existing).
        const vis = new Map(currentState.visibility);
        initBlendVisibility(newObjects, vis);

        // Initialize shape key weights for new sets (preserve existing)
        const weights = new Map(currentState.shapeKeyWeights);
        for (const [setName, skSet] of remappedSKSets) {
          if (!weights.has(setName)) {
            const kw = new Map<string, number>();
            for (const key of skSet.keys) kw.set(key.name, key.defaultValue);
            weights.set(setName, kw);
          }
        }

        // Rebuild driver graph from ALL entries' shape key sets
        const mergedSKSets = allShapeKeySets(newFiles);
        const driverGraph = new DriverGraph();
        driverGraph.build(mergedSKSets);
        driverGraph.evaluate(weights);

        set({
          blendFiles: newFiles,
          visibility: vis,
          shapeKeyWeights: weights,
          driverGraph,
          loading: false,
        });
      } catch (e) {
        set({
          error: e instanceof Error ? e.message : String(e),
          loading: false,
        });
      }
    },
  };
}
